import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES, LANGUAGE_NAMES, type SupportedLanguage } from "../../lib/i18n";
import { ChevronDownIcon } from "../icons";
import styles from "./LanguageSwitcher.module.css";

interface LanguageSwitcherProps {
  variant?: "header" | "standalone";
}

export function LanguageSwitcher({ variant = "header" }: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const currentLanguage = i18n.language as SupportedLanguage;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const handleLanguageChange = (lang: SupportedLanguage) => {
    i18n.changeLanguage(lang);
    setIsOpen(false);
  };

  const containerClass = variant === "header" ? styles.containerHeader : styles.containerStandalone;

  return (
    <div className={containerClass} ref={menuRef}>
      <button
        className={styles.button}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={t("language.select")}
      >
        <span className={styles.flag}>{getFlagEmoji(currentLanguage)}</span>
        <span className={styles.label}>{LANGUAGE_NAMES[currentLanguage]}</span>
        <ChevronDownIcon className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`} />
      </button>

      {isOpen && (
        <div className={styles.dropdown} role="listbox">
          {SUPPORTED_LANGUAGES.map((lang) => (
            <button
              key={lang}
              className={`${styles.option} ${lang === currentLanguage ? styles.optionActive : ""}`}
              onClick={() => handleLanguageChange(lang)}
              role="option"
              aria-selected={lang === currentLanguage}
            >
              <span className={styles.flag}>{getFlagEmoji(lang)}</span>
              <span>{LANGUAGE_NAMES[lang]}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function getFlagEmoji(lang: SupportedLanguage): string {
  const flags: Record<SupportedLanguage, string> = {
    en: "EN",
    es: "ES",
  };
  return flags[lang];
}
